import { FC, useEffect, useMemo, useState } from "react";
import useOrderOptimizerList from "../../../../../common/services/order-optimizer/list/use-order-optimizer-list";
import useAbort from "../../../../../common/hooks/use-abort";
import orderOptimizerTranslationsHelper from "../../../../../languages/order-optimizer-translations.helper";
import OrderOptimizerRailyListingTableComponent from "../../../../order-optimizer/raily/listing/table/order-optimizer-raily-listing-table.component";
import orderOptimzierRailyListingHelper from "../../../../order-optimizer/raily/listing/common/order-optimizer-raily-listing.helper";
import StatusLegendComponent from "../../../../../common/components/status-legend/status-legend.component";
import { OrderOptimizerRailyListingItem } from "../../../../order-optimizer/raily/listing/common/types/order-optimizer-raily-listing-item";
import orderOptimizerRailyListingItemFactory from "../../../../order-optimizer/raily/listing/common/order-optimizer-raily-listing-item.factory";
import CardComponent from "../../../../../common/components/card/card.component";
import OrderOptimizerListParams from "../../../../../common/services/order-optimizer/list/order-optimizer-list-params";
import ListingPaginationComponent from "../../../../../common/components/listing/pagination/listing-pagination.component";
import usePagination from "../../../../../common/hooks/use-pagination";
import OrderDetailsManageDriverAssignmentOptitoolFiltersComponent from "./filters/order-details-manage-driver-assignment-optitool-filters.component";
import OrderDetailsManageDriverAssignmentOptitoolFilter from "./filters/types/order-details-manage-driver-assignment-optitool-filter";
import orderDetailsManageDriverAssignmentOptitoolLoadParamsFactory from "./order-details-manage-driver-assignment-optitool-load-params.factory";
import OrderDetailsManageDriverAssignmentOptitoolFilterType from "./filters/types/order-details-manage-driver-assignment-optitool-filter-type";

type Props = {
  orderHumanId: number;
};

const OrderDetailsManageDriverAssignmentOptitoolComponent: FC<Props> = (
  props,
) => {
  const translations =
    orderOptimizerTranslationsHelper.getRailyListingTranslations();

  const orderOptimizerList = useOrderOptimizerList();
  const orderOptimizerListAbort = useAbort();

  const [filters, setFilters] = useState<
    OrderDetailsManageDriverAssignmentOptitoolFilter[]
  >([
    {
      type: OrderDetailsManageDriverAssignmentOptitoolFilterType.ORDER_HUMAN_ID,
      value: props.orderHumanId,
    },
  ]);

  const { page, pageSize, setPage, setPageSize } = usePagination({
    totalResults: orderOptimizerList.data.totalCount,
    defaultPageSize: 10,
  });

  const loadListingItems = () => {
    const params: OrderOptimizerListParams =
      orderDetailsManageDriverAssignmentOptitoolLoadParamsFactory.create(
        filters,
        page,
        pageSize,
      );

    orderOptimizerList.load(params, orderOptimizerListAbort.signal);
  };

  useEffect(() => {
    loadListingItems();

    return orderOptimizerListAbort.revoke;
  }, [page, pageSize, filters]);

  const listingItems: OrderOptimizerRailyListingItem[] = useMemo(() => {
    return orderOptimizerRailyListingItemFactory.createListingItems(
      orderOptimizerList.data.data,
    );
  }, [orderOptimizerList.data]);

  const statusOptions = orderOptimzierRailyListingHelper.getStatusOptions();

  return (
    <div className="order_details_manage_driver_assignment_extended_optitool">
      <h4>{translations.header.headingLabel}</h4>
      <StatusLegendComponent statusData={statusOptions} />
      <OrderDetailsManageDriverAssignmentOptitoolFiltersComponent
        filters={filters}
        onFiltersChange={(filters) => {
          setFilters(filters);
          setPage(1);
        }}
      />
      <CardComponent
        classNames={{
          root: "order_details_manage_driver_assignment_extended_optitool_table",
        }}
      >
        <OrderOptimizerRailyListingTableComponent
          listingItems={listingItems}
          selectedHumanId={props.orderHumanId}
          isError={orderOptimizerList.isError}
          isLoading={orderOptimizerList.isLoading}
          applyOptimizationStatus={orderOptimizerList.applyOptimizationStatus}
        />
        <ListingPaginationComponent
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          page={page}
          pageSize={pageSize}
          totalResults={orderOptimizerList.data.totalCount}
        />
      </CardComponent>
    </div>
  );
};

export default OrderDetailsManageDriverAssignmentOptitoolComponent;
